// Gemeinsame Sammlungen (Phase 6): anlegen, Mitglieder, Einladungen,
// verlassen. Die Sammlungsliste selbst kommt weiter aus /me
// (lib/me.ts), hier nur, was zusaetzlich dazukommt.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api.js";
import type { Sammlung } from "./me.js";

export interface Mitglied {
  userId: string;
  displayName: string;
  role: "owner" | "member";
  joinedAt: string;
}

export function useMitglieder(sammlungId: number | undefined) {
  return useQuery({
    queryKey: ["collection-members", sammlungId],
    enabled: sammlungId !== undefined,
    queryFn: async () =>
      (
        await api<{ mitglieder: Mitglied[] }>(
          `/collections/${sammlungId}/members`,
        )
      ).mitglieder,
  });
}

export function useSammlungAnlegen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api<{ sammlung: Sammlung }>("/collections", {
        method: "POST",
        body: { name },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["me"] }),
  });
}

/**
 * Sammlung umbenennen. Den Endpunkt gibt es serverseitig seit Phase 6, nur
 * gerufen hat ihn nie jemand: die Einstellungen zeigten den Namen an, ohne
 * ihn aendern zu lassen.
 */
export function useSammlungUmbenennen(sammlungId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api<{ id: number; name: string }>(`/collections/${sammlungId}`, {
        method: "PATCH",
        body: { name },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useEinladungErstellen(sammlungId: number | undefined) {
  return useMutation({
    mutationFn: () =>
      api<{ token: string }>(`/collections/${sammlungId}/invites`, {
        method: "POST",
      }),
  });
}

export function useSammlungVerlassen(sammlungId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<void>(`/collections/${sammlungId}/members/verlassen`, {
        method: "POST",
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["me"] }),
  });
}

export interface EinladungsVorschau {
  sammlungName: string;
  /** Der Link ist eingeloest und der Anfragende ist schon Mitglied. */
  schonDabei?: boolean;
}

export function useEinladungsVorschau(token: string | undefined) {
  return useQuery({
    queryKey: ["sammlungseinladung", token],
    enabled: token !== undefined,
    queryFn: () => api<EinladungsVorschau>(`/sammlungseinladung/${token}`),
    retry: false,
  });
}

export function useSammlungBeitreten() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      api<{ collectionId: number; schonDabei?: boolean }>(
        `/sammlungseinladung/${token}/annehmen`,
        { method: "POST" },
      ),
    // Beitreten schaltet die Sammlung serverseitig aktiv, die Antwort auf
    // /me traegt danach ein anderes activeCollectionId - deshalb neu holen.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["me"] }),
  });
}

/** Die Sammlung, in die der Hauptscreen gerade schreibt (Phase 6). */
export function aktiveSammlung(
  sammlungen: Sammlung[],
  activeCollectionId: number | undefined,
): Sammlung | undefined {
  const gewaehlt =
    activeCollectionId !== undefined
      ? sammlungen.find((s) => s.id === activeCollectionId)
      : undefined;
  return gewaehlt ?? sammlungen.find((s) => s.kind === "personal");
}
